import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Sparkles, BookOpen, Bell, Map, Rocket, GitCommitHorizontal,
  Calendar, Loader2, Power, ArrowRight, Lock, Check,
  LayoutDashboard, BarChart3, Users, ShoppingBag, Wrench, Package, Store,
  Heart, QrCode, Image as ImageIcon, FileText, HelpCircle, FolderOpen, MapPin,
  TrendingUp, Link2, PhoneCall, Palette, Layout, CreditCard, LifeBuoy, Settings as SettingsIcon,
  HardDrive, Smartphone, Shield,
} from "lucide-react";
import { toast } from "sonner";

// COCKPIT côté client = LECTURE SEULE.
// L'administration (CRUD releases / bugs / roadmap / docs) vit uniquement sur /agency/cockpit.

const LUCIDE_ICONS = {
  LayoutDashboard, BarChart3, GitCommitHorizontal, Users, ShoppingBag, Calendar,
  Wrench, Package, Store, Heart, QrCode, Image: ImageIcon, FileText, HelpCircle,
  FolderOpen, MapPin, TrendingUp, Sparkles, Link2, PhoneCall, Shield, Palette,
  Layout, CreditCard, LifeBuoy, Settings: SettingsIcon, HardDrive, Rocket, Smartphone,
};

const STATUS_LABELS = {
  planned: "Planifié", in_progress: "En cours", released: "Livré", cancelled: "Annulé",
};

const TABS = [
  { key: "changelog", label: "Quoi de neuf", icon: Sparkles },
  { key: "features", label: "Fonctionnalités actives", icon: Power },
  { key: "docs", label: "Documentation", icon: BookOpen },
  { key: "roadmap", label: "Roadmap", icon: Map },
];

export default function CockpitAdmin() {
  const [tab, setTab] = useState("changelog");
  const [meta, setMeta] = useState(null);

  const loadMeta = async () => {
    try { const { data } = await api.get("/cockpit/meta"); setMeta(data); } catch {}
  };
  useEffect(() => { loadMeta(); }, []);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="cockpit-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <GitCommitHorizontal className="w-3.5 h-3.5" /> COCKPIT
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">
            {meta?.product_name || "COCKPIT by Agence MSC"}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Votre centre d'information : version actuelle, nouveautés livrées et feuille de route à venir.
          </p>
        </div>
        {meta && (
          <div className="flex flex-col items-end gap-1.5">
            <Badge className="bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white border-0 text-xs font-bold px-3 py-1.5">
              <Rocket className="w-3 h-3 mr-1" /> Votre version · v{meta.version}
            </Badge>
            {meta.unread_count > 0 && (
              <Badge className="bg-red-500 text-white border-0 text-[10px] uppercase tracking-wider animate-pulse">
                {meta.unread_count} nouveauté{meta.unread_count > 1 ? "s" : ""} à lire
              </Badge>
            )}
          </div>
        )}
      </header>

      <div className="flex items-center gap-1 border-b border-[hsl(var(--border))] overflow-x-auto" data-testid="cockpit-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
              tab === t.key
                ? "border-[color:var(--brand-sky)] text-[color:var(--brand-navy)]"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
            }`}
            data-testid={`cockpit-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "changelog" && <ChangelogPanel meta={meta} onRead={loadMeta} />}
      {tab === "features" && <FeaturesPanel />}
      {tab === "docs" && <DocsPanel />}
      {tab === "roadmap" && <RoadmapPanel />}
    </div>
  );
}

// ==================== CHANGELOG (lecture seule) ====================
function ChangelogPanel({ meta, onRead }) {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/cockpit/releases"); setReleases(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async () => {
    if (!meta?.latest_release) return;
    try {
      await api.post("/cockpit/mark-read", { version: meta.latest_release.version });
      toast.success("Marqué comme lu");
      onRead && onRead();
    } catch {}
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!releases.length) return <EmptyState icon={Sparkles} title="Aucune version publiée" />;

  return (
    <div className="space-y-6">
      {meta?.unread_count > 0 && (
        <Card className="p-4 bg-gradient-to-br from-[color:var(--brand-sky)]/10 to-[color:var(--brand-navy)]/5 border-[color:var(--brand-sky)]/40 flex items-center justify-between gap-3 flex-wrap" data-testid="unread-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky)]/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[color:var(--brand-sky)]" />
            </div>
            <div>
              <div className="font-bold text-[color:var(--brand-navy)]">
                {meta.unread_count} nouveauté{meta.unread_count > 1 ? "s" : ""} disponible{meta.unread_count > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Découvrez les dernières améliorations du COCKPIT</div>
            </div>
          </div>
          <Button size="sm" onClick={markRead} className="bg-[color:var(--brand-navy)]" data-testid="mark-read-btn">
            <Check className="w-3.5 h-3.5 mr-1.5" /> Tout marquer comme lu
          </Button>
        </Card>
      )}

      <div className="space-y-4">
        {releases.map((r, idx) => (
          <Card key={r.id} className={`p-5 ${r.is_current ? "border-2 border-[color:var(--brand-sky)]" : ""}`} data-testid={`release-${r.version}`}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[color:var(--brand-navy)] text-white border-0 text-sm font-mono font-bold">v{r.version}</Badge>
                  {r.is_current && <Badge className="bg-emerald-500 text-white border-0 text-[10px] uppercase tracking-wider">Actuelle</Badge>}
                  {idx === 0 && !r.is_current && <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Latest</Badge>}
                </div>
                <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mt-2">{r.title}</h3>
                <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(r.released_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
              </div>
            </div>
            {r.summary && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{r.summary}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {r.features?.length > 0 && <Section title="✨ Nouveautés" items={r.features} color="text-emerald-700 bg-emerald-50" />}
              {r.improvements?.length > 0 && <Section title="⬆ Améliorations" items={r.improvements} color="text-blue-700 bg-blue-50" />}
              {r.fixes?.length > 0 && <Section title="🐛 Corrections" items={r.fixes} color="text-amber-700 bg-amber-50" />}
              {r.security?.length > 0 && <Section title="🔒 Sécurité" items={r.security} color="text-red-700 bg-red-50" />}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Section({ title, items, color }) {
  return (
    <div className={`rounded-lg p-3 ${color}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-1.5">{title}</div>
      <ul className="text-xs space-y-1">
        {items.map((it, i) => <li key={i} className="flex gap-1.5"><span className="shrink-0">·</span><span>{it}</span></li>)}
      </ul>
    </div>
  );
}

// ==================== DOCS (lecture seule) ====================
function DocsPanel() {
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);

  const load = async () => {
    const { data } = await api.get("/cockpit/docs");
    setDocs(data);
    if (data.length && !selected) setSelected(data[0].slug);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/cockpit/docs/${selected}`).then(r => setContent(r.data)).catch(() => setContent(null));
  }, [selected]);

  const groupedDocs = useMemo(() => {
    const g = {};
    docs.forEach(d => { (g[d.category] = g[d.category] || []).push(d); });
    return g;
  }, [docs]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5" data-testid="cockpit-docs-panel">
      <Card className="p-4 h-fit sticky top-4">
        <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--brand-navy)] mb-3">Sommaire</div>
        <div className="space-y-3">
          {Object.entries(groupedDocs).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1 font-bold">{cat}</div>
              <div className="space-y-0.5">
                {items.sort((a, b) => a.order - b.order).map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelected(d.slug)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition ${
                      selected === d.slug
                        ? "bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-navy)] font-bold"
                        : "hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]"
                    }`}
                    data-testid={`doc-nav-${d.slug}`}
                  >
                    {d.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 min-h-[400px]">
        {content ? (
          <div>
            <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-4">{content.title}</h2>

            {content.video_url && (
              <div className="mb-6" data-testid="doc-main-video">
                <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                  <iframe
                    src={content.video_url}
                    title={content.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {content.video_steps?.length > 0 && (
              <div className="mb-6" data-testid="doc-video-steps">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-3">
                  Tutoriels vidéo étape par étape
                </div>
                <div className="space-y-3">
                  {content.video_steps.map((step, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 p-3 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]">
                      <div className="aspect-video rounded-lg overflow-hidden bg-black">
                        <iframe src={step.youtube_url} title={step.title} className="w-full h-full" frameBorder="0" allow="encrypted-media; picture-in-picture" allowFullScreen />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[color:var(--brand-sky)] text-white text-[10px] font-bold">{i + 1}</span>
                          <span className="font-bold text-[color:var(--brand-navy)] text-sm">{step.title}</span>
                          {step.duration && <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">· {step.duration}</span>}
                        </div>
                        {step.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{step.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Markdown text={content.content || ""} />
            <div className="mt-6 pt-4 border-t text-[11px] text-[hsl(var(--muted-foreground))]">
              Dernière mise à jour : {new Date(content.updated_at).toLocaleDateString("fr-FR")}
            </div>
          </div>
        ) : (
          <EmptyState icon={BookOpen} title="Sélectionnez un document" />
        )}
      </Card>
    </div>
  );
}

function Markdown({ text }) {
  const html = useMemo(() => {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/^### (.*$)/gm, '<h3 class="font-bold text-base text-[color:var(--brand-navy)] mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="font-bold text-lg text-[color:var(--brand-navy)] mt-6 mb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="font-display font-bold text-xl text-[color:var(--brand-navy)] mt-2 mb-3">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 list-decimal">$2</li>')
      .replace(/\n\n/g, '</p><p class="my-2">')
      .replace(/`([^`]+)`/g, '<code class="bg-[hsl(var(--muted))] px-1 rounded text-xs font-mono">$1</code>');
  }, [text]);
  return <div className="prose prose-sm max-w-none text-[hsl(var(--foreground))] leading-relaxed" dangerouslySetInnerHTML={{ __html: `<p class="my-2">${html}</p>` }} />;
}

// ==================== ROADMAP (lecture seule) ====================
function RoadmapPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cockpit/roadmap")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = { P0: [], P1: [], P2: [], P3: [] };
    items.forEach(i => { (g[i.priority] = g[i.priority] || []).push(i); });
    return g;
  }, [items]);

  const statusBadge = {
    planned: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-100 text-blue-700",
    released: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
  };
  const priorityLabel = { P0: "Critique", P1: "Importante", P2: "Améliorations", P3: "Idées & long terme" };
  const priorityColor = { P0: "bg-red-600", P1: "bg-orange-500", P2: "bg-amber-400", P3: "bg-slate-400" };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const released = items.filter(i => i.status === "released");
  const upcoming = items.filter(i => i.status !== "released" && i.status !== "cancelled");

  if (!items.length) return <EmptyState icon={Map} title="Roadmap non disponible" />;

  return (
    <div className="space-y-5" data-testid="roadmap-panel">
      <Card className="p-5 bg-gradient-to-br from-[color:var(--brand-sky)]/10 to-[color:var(--brand-navy)]/5 border-[color:var(--brand-sky)]/30">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-lg bg-[color:var(--brand-sky)] text-white flex items-center justify-center shrink-0">
            <Map className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Feuille de route du COCKPIT</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              {upcoming.length} fonctionnalité{upcoming.length > 1 ? "s" : ""} à venir · {released.length} déjà livrée{released.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </Card>

      {["P0", "P1", "P2", "P3"].map(pri => grouped[pri]?.length > 0 && (
        <div key={pri}>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`text-xs font-bold ${priorityColor[pri]} text-white border-0`}>{pri}</Badge>
            <span className="text-xs font-bold text-[color:var(--brand-navy)] uppercase tracking-wider">{priorityLabel[pri]}</span>
          </div>
          <div className="space-y-2">
            {grouped[pri].map(r => (
              <Card key={r.id} className="p-4" data-testid={`roadmap-item-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[color:var(--brand-navy)]">{r.title}</span>
                      <Badge className={`text-[9px] uppercase border-0 ${statusBadge[r.status] || ""}`}>{STATUS_LABELS[r.status] || r.status}</Badge>
                      {r.target_version && <Badge variant="outline" className="text-[9px]">→ v{r.target_version}</Badge>}
                      {r.category && <Badge variant="outline" className="text-[9px]">{r.category}</Badge>}
                    </div>
                    {r.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{r.description}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2" />
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{title}</div>
    </div>
  );
}

// ==================== FEATURES (inventaire actif — lecture) ====================
function FeaturesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cockpit/features").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return <EmptyState icon={Power} title="Impossible de charger les fonctionnalités" />;

  return (
    <div className="space-y-5" data-testid="features-panel">
      <Card className="p-5 bg-gradient-to-br from-[color:var(--brand-sky)]/10 to-[color:var(--brand-navy)]/5 border-[color:var(--brand-sky)]/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1 flex items-center gap-1.5">
              <Power className="w-3 h-3" /> Inventaire des modules
            </div>
            <div className="font-display text-xl font-bold text-[color:var(--brand-navy)]">
              {data.active_features} fonctionnalité{data.active_features > 1 ? "s" : ""} active{data.active_features > 1 ? "s" : ""} sur {data.total_features}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              Activez ou désactivez les modules dans leurs pages respectives
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-16 h-16 shrink-0">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="3" className="text-[hsl(var(--muted))]" />
                <circle cx="18" cy="18" r="16" fill="none" strokeWidth="3" strokeLinecap="round" stroke="url(#grad)" strokeDasharray={`${data.activation_rate}, 100`} />
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--brand-sky)" />
                    <stop offset="100%" stopColor="var(--brand-navy)" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[color:var(--brand-navy)]">
                {data.activation_rate}%
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        {data.families.map(family => (
          <div key={family.name}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full" style={{ background: family.color, boxShadow: `0 0 8px ${family.color}` }} />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: family.color }}>{family.name}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                {family.features.filter(f => f.enabled).length} / {family.features.length} actives
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {family.features.map(feat => <FeatureCard key={feat.key} feat={feat} color={family.color} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ feat, color }) {
  const IconC = LUCIDE_ICONS[feat.icon] || Sparkles;
  const isOn = !!feat.enabled;
  const isLocked = !!feat.locked;
  return (
    <div
      className={`relative rounded-xl border p-3.5 transition-all ${
        isOn
          ? "bg-white border-[hsl(var(--border))] hover:shadow-md"
          : "bg-[hsl(var(--muted))]/30 border-dashed border-[hsl(var(--border))] opacity-70 hover:opacity-100"
      }`}
      data-testid={`feature-card-${feat.key}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isOn ? "text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
          style={isOn ? { background: color, boxShadow: `0 4px 12px ${color}40` } : {}}
        >
          <IconC className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-[color:var(--brand-navy)]">{feat.label}</span>
            {isLocked && <Lock className="w-2.5 h-2.5 text-slate-400" title="Toujours actif — module cœur" />}
          </div>
          {feat.description && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{feat.description}</p>}
          {feat.stats && <div className="text-[10px] font-mono text-[color:var(--brand-sky)] mt-1">{feat.stats}</div>}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <Badge className={`text-[9px] uppercase tracking-wider border-0 ${isOn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {isOn ? "Actif" : "Inactif"}
          </Badge>
          {(feat.route || feat.toggle_route) && (
            <NavLink
              to={feat.toggle_route || feat.route}
              className="text-[10px] text-[color:var(--brand-sky)] hover:underline flex items-center gap-0.5 font-semibold"
              data-testid={`feature-link-${feat.key}`}
            >
              {feat.toggle_route && !isLocked ? "Configurer" : "Ouvrir"} <ArrowRight className="w-2.5 h-2.5" />
            </NavLink>
          )}
        </div>
      </div>
    </div>
  );
}
